import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis,
  Tooltip, CartesianGrid, ReferenceLine
} from 'recharts';

interface PricePoint {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface ChartDataPoint extends PricePoint {
  sma50?: number;
  sma200?: number;
  bbUpper?: number;
  bbMiddle?: number;
  bbLower?: number;
  tenkan?: number;
  kijun?: number;
  senkouA?: number;
  senkouB?: number;
}

interface StockTechnicalChartProps {
  ticker: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function calcSMA(data: number[], period: number): (number | undefined)[] {
  return data.map((_, i) => {
    if (i < period - 1) return undefined;
    const slice = data.slice(i - period + 1, i + 1);
    return slice.reduce((a, b) => a + b, 0) / period;
  });
}

function calcBollinger(closes: number[], period = 20, mult = 2) {
  const sma = calcSMA(closes, period);
  return closes.map((_, i) => {
    if (i < period - 1 || sma[i] === undefined) return { upper: undefined, middle: undefined, lower: undefined };
    const slice = closes.slice(i - period + 1, i + 1);
    const mean = sma[i]!;
    const variance = slice.reduce((sum, v) => sum + (v - mean) ** 2, 0) / period;
    const stdDev = Math.sqrt(variance);
    return { upper: mean + mult * stdDev, middle: mean, lower: mean - mult * stdDev };
  });
}

function calcIchimoku(highs: number[], lows: number[], closes: number[]) {
  const highLow = (start: number, end: number) => {
    const h = highs.slice(start, end);
    const l = lows.slice(start, end);
    if (!h.length) return undefined;
    return (Math.max(...h) + Math.min(...l)) / 2;
  };

  const len = closes.length;
  const tenkan: (number | undefined)[] = [];
  const kijun: (number | undefined)[] = [];
  const senkouA: (number | undefined)[] = [];
  const senkouB: (number | undefined)[] = [];

  for (let i = 0; i < len; i++) {
    tenkan.push(i >= 8 ? highLow(i - 8, i + 1) : undefined);
    kijun.push(i >= 25 ? highLow(i - 25, i + 1) : undefined);
    const t = tenkan[i], k = kijun[i];
    senkouA.push(t !== undefined && k !== undefined ? (t + k) / 2 : undefined);
    senkouB.push(i >= 51 ? highLow(i - 51, i + 1) : undefined);
  }

  return { tenkan, kijun, senkouA, senkouB };
}

export function StockTechnicalChart({ ticker, open, onOpenChange }: StockTechnicalChartProps) {
  const [period, setPeriod] = useState<'1m' | '6m' | '1y'>('6m');
  const [rawData, setRawData] = useState<PricePoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const monthsMap = { '1m': 2, '6m': 7, '1y': 14 }; // extra months for indicator warmup

  useEffect(() => {
    if (!open || !ticker) return;
    const fetchPrices = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fnError } = await supabase.functions.invoke('fetch-price-history', {
          body: { ticker, months: monthsMap[period] }
        });
        if (fnError) throw fnError;
        if (!data?.success) throw new Error(data?.error || 'Failed');
        setRawData(data.prices || []);
      } catch (e: any) {
        setError(e.message);
        setRawData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPrices();
  }, [open, ticker, period]);

  const chartData = useMemo(() => {
    if (!rawData.length) return [];
    const closes = rawData.map(p => p.close);
    const highs = rawData.map(p => p.high);
    const lows = rawData.map(p => p.low);

    const sma50 = calcSMA(closes, 50);
    const sma200 = calcSMA(closes, 200);
    const bb = calcBollinger(closes);
    const ich = calcIchimoku(highs, lows, closes);

    const full: ChartDataPoint[] = rawData.map((p, i) => ({
      ...p,
      sma50: sma50[i],
      sma200: sma200[i],
      bbUpper: bb[i].upper,
      bbMiddle: bb[i].middle,
      bbLower: bb[i].lower,
      tenkan: ich.tenkan[i],
      kijun: ich.kijun[i],
      senkouA: ich.senkouA[i],
      senkouB: ich.senkouB[i],
    }));

    // Trim warmup period based on display
    const displayMonths = { '1m': 1, '6m': 6, '1y': 12 }[period];
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - displayMonths);
    const cutoffStr = cutoff.toISOString().split('T')[0];
    return full.filter(p => p.date >= cutoffStr);
  }, [rawData, period]);

  const priceChange = chartData.length >= 2
    ? ((chartData[chartData.length - 1].close - chartData[0].close) / chartData[0].close * 100)
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-mono text-lg">{ticker}</span>
            <Badge variant={priceChange >= 0 ? 'default' : 'destructive'} className="font-mono">
              {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
            </Badge>
            <span className="text-sm text-muted-foreground font-normal">Technical Analysis</span>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={period} onValueChange={(v) => setPeriod(v as any)}>
          <TabsList className="grid w-48 grid-cols-3">
            <TabsTrigger value="1m">1M</TabsTrigger>
            <TabsTrigger value="6m">6M</TabsTrigger>
            <TabsTrigger value="1y">1Y</TabsTrigger>
          </TabsList>

          <div className="mt-4">
            {loading && (
              <div className="space-y-2">
                <Skeleton className="h-[400px] w-full" />
              </div>
            )}
            {error && <p className="text-sm text-destructive text-center py-8">{error}</p>}
            {!loading && !error && chartData.length > 0 && (
              <div className="space-y-4">
                {/* Main chart */}
                <div className="h-[420px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                        interval="preserveStartEnd"
                        minTickGap={40}
                      />
                      <YAxis
                        domain={['auto', 'auto']}
                        tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(v) => `$${v.toFixed(0)}`}
                        width={55}
                      />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0]?.payload as ChartDataPoint;
                          return (
                            <div className="bg-background border border-border rounded-lg px-3 py-2 shadow-xl text-xs space-y-1">
                              <p className="font-medium">{new Date(label).toLocaleDateString('en', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-muted-foreground">
                                <span>Open: <span className="text-foreground font-mono">${d.open?.toFixed(2)}</span></span>
                                <span>High: <span className="text-foreground font-mono">${d.high?.toFixed(2)}</span></span>
                                <span>Low: <span className="text-foreground font-mono">${d.low?.toFixed(2)}</span></span>
                                <span>Close: <span className="text-foreground font-mono">${d.close?.toFixed(2)}</span></span>
                                {d.sma50 && <span>SMA50: <span className="text-blue-400 font-mono">${d.sma50.toFixed(2)}</span></span>}
                                {d.sma200 && <span>SMA200: <span className="text-orange-400 font-mono">${d.sma200.toFixed(2)}</span></span>}
                                {d.bbUpper && <span>BB Upper: <span className="text-purple-400 font-mono">${d.bbUpper.toFixed(2)}</span></span>}
                                {d.bbLower && <span>BB Lower: <span className="text-purple-400 font-mono">${d.bbLower.toFixed(2)}</span></span>}
                              </div>
                            </div>
                          );
                        }}
                      />

                      {/* Ichimoku Cloud (Kumo) */}
                      <Area dataKey="senkouA" stroke="none" fill="rgba(34,197,94,0.08)" stackId="kumo" />
                      <Area dataKey="senkouB" stroke="none" fill="rgba(239,68,68,0.08)" stackId="kumo2" />

                      {/* Bollinger Bands */}
                      <Line dataKey="bbUpper" stroke="#a855f7" strokeWidth={1} dot={false} strokeDasharray="4 2" opacity={0.6} />
                      <Line dataKey="bbMiddle" stroke="#a855f7" strokeWidth={1} dot={false} strokeDasharray="2 2" opacity={0.4} />
                      <Line dataKey="bbLower" stroke="#a855f7" strokeWidth={1} dot={false} strokeDasharray="4 2" opacity={0.6} />

                      {/* SMA */}
                      <Line dataKey="sma50" stroke="#3b82f6" strokeWidth={1.5} dot={false} />
                      <Line dataKey="sma200" stroke="#f97316" strokeWidth={1.5} dot={false} />

                      {/* Ichimoku lines */}
                      <Line dataKey="tenkan" stroke="#22c55e" strokeWidth={1} dot={false} opacity={0.7} />
                      <Line dataKey="kijun" stroke="#ef4444" strokeWidth={1} dot={false} opacity={0.7} />

                      {/* Price */}
                      <Line dataKey="close" stroke="#FACC15" strokeWidth={2.5} dot={false} name="Price" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-3 justify-center text-[11px]">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#FACC15] inline-block rounded" /> Price</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#3b82f6] inline-block rounded" /> SMA 50</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#f97316] inline-block rounded" /> SMA 200</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#a855f7] inline-block rounded" /> Bollinger</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#22c55e] inline-block rounded" /> Tenkan</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#ef4444] inline-block rounded" /> Kijun</span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 bg-green-500/10 border border-green-500/30 inline-block rounded-sm" /> Kumo
                  </span>
                </div>

                {/* Current indicators summary */}
                {chartData.length > 0 && (() => {
                  const last = chartData[chartData.length - 1];
                  return (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div className="bg-muted/30 rounded-lg px-3 py-2">
                        <span className="text-muted-foreground">Close</span>
                        <p className="font-mono font-bold text-foreground">${last.close.toFixed(2)}</p>
                      </div>
                      {last.sma50 && (
                        <div className="bg-muted/30 rounded-lg px-3 py-2">
                          <span className="text-muted-foreground">SMA 50</span>
                          <p className="font-mono font-bold" style={{ color: last.close > last.sma50 ? '#22c55e' : '#ef4444' }}>
                            ${last.sma50.toFixed(2)}
                          </p>
                        </div>
                      )}
                      {last.sma200 && (
                        <div className="bg-muted/30 rounded-lg px-3 py-2">
                          <span className="text-muted-foreground">SMA 200</span>
                          <p className="font-mono font-bold" style={{ color: last.close > last.sma200 ? '#22c55e' : '#ef4444' }}>
                            ${last.sma200.toFixed(2)}
                          </p>
                        </div>
                      )}
                      {last.bbUpper && last.bbLower && (
                        <div className="bg-muted/30 rounded-lg px-3 py-2">
                          <span className="text-muted-foreground">BB Width</span>
                          <p className="font-mono font-bold text-purple-400">
                            {((last.bbUpper - last.bbLower) / (last.bbMiddle || 1) * 100).toFixed(1)}%
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
